import React, { useEffect, useState } from "react";
import DatepickerComponent from "../../components/datepicker/datepicker.component";
import InputComponent from "../../components/input/input.component";
import SelectComponent from "../../components/select/select.component";
import TextareaComponent from "../../components/textarea/textarea.component";
import CustomerForm from "./customer.form";
import usePeriod from "../../hooks/period.use";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import AlertPeriod from "../../components/alert-period/alert-period";
import InvalidFeedback from "../../components/invalid-feedback/invalid-feedback.component";
import { PackingsService } from "../../services/packings";
import { UtilsService } from "../../services/utils";
import Button from "../../components/button/button.component";
import { to } from "../../polyfill";
import { RegistersService } from "../../services/operation/registers";
import { useNavigate } from "react-router-dom";

function Step1({ corte, next }: any) {
  const navigate = useNavigate();

  const { currentPeriod } = usePeriod();

  const [showModal, setShowModal] = useState({
    data: null,
    show: false,
  });
  const [packingList, setPackingList] = useState<any[]>([]);

  const defaultValues = {
    _id: "",
    period: currentPeriod._id,
    general: {
      date: "",
      procedencia: "",
      folio_pesada: "",
      packing: "",
      total_cajas: "",
      peso_caja: "",
      peso_neto: "",
      observaciones: "",
    },
    customers: [],
  };

  const {
    setValue,
    control,
    register,
    reset,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<typeof defaultValues>({
    mode: "all",
    defaultValues,
  });

  const { peso_neto, total_cajas } = watch("general");

  const { fields, prepend, remove, update } = useFieldArray<any>({
    control,
    name: "customers",
    rules: {
      minLength: 1,
    },
  });

  const submit = async (form: typeof defaultValues) => {
    const [error, data] = await to(RegistersService.saveStep1(form));
    if (error) {
      alert(error.message);
    } else {
      navigate("/register");
    }
  };

  const getPackingList = async () => {
    const data = await PackingsService.list();
    setPackingList(
      data
        .filter((d: any) => d.status)
        .map((row: any) => ({ value: row._id, text: row.name }))
    );
  };

  useEffect(() => {
    getPackingList();
  }, []);

  useEffect(() => {
    setValue("general.packing", corte?.general?.packing?._id || "");
  }, [packingList, corte]);

  useEffect(() => {
    setValue(
      "general.peso_caja",
      +total_cajas === 0
        ? "0"
        : `${UtilsService.round(+peso_neto / +total_cajas)}`
    );
  }, [peso_neto, total_cajas]);

  useEffect(() => {
    if (corte) {
      reset({
        _id: corte._id,
        period: currentPeriod._id,
        general: {
          date: UtilsService.dateFormat(corte.general.date, "YYYY-MM-DD"),
          procedencia: corte.general.procedencia,
          folio_pesada: corte.general.folio_pesada,
          packing: corte.general.packing._id,
          total_cajas: corte.general.total_cajas,
          peso_caja: corte.general.peso_caja,
          peso_neto: corte.general.peso_neto,
          observaciones: corte.general.observaciones,
        },
      });

      const customers = corte.customers.map((customer: any, index: number) => ({
        index: index++,
        customer: customer.customer._id,
        customer_name: customer.customer.name,
        field: customer.field._id,
        field_name: customer.field.name,
        cajas: customer.cajas,
        peso_caja: customer.peso_caja,
        kg_totales: customer.kg_totales,
      }));

      setTimeout(() => prepend(customers), 100);
    }
  }, [corte]);

  return (
    <>
      <div className="">
        <h2
          className="fw-bold d-flex align-items-center text-gray-900"
          onClick={() => console.log(errors)}
        >
          General
        </h2>
        <div className="text-muted fw-semibold fs-6">
          Ingresa la información general del corte, además de registrar las
          cajas por productor.
        </div>
      </div>

      <div className="my-6">
        <AlertPeriod />
      </div>

      <div className="row mb-5">
        <div className="col-6">
          <div className="">
            <Controller
              name="general.date"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <DatepickerComponent
                    label="Fecha *"
                    errors={error}
                    onChange={onChange}
                    value={value}
                  />
                  <InvalidFeedback error={error} />
                </>
              )}
            />
          </div>
        </div>
        <div className="col-6">
          <div className="">
            <InputComponent
              label="Procedencia *"
              {...register("general.procedencia", { required: true })}
              errors={errors.general?.procedencia}
            />
          </div>
        </div>
      </div>
      <div className="row mb-5">
        <div className="col-6">
          <div className="">
            <InputComponent
              label="Folio de pesada *"
              {...register("general.folio_pesada", { required: true })}
              errors={errors.general?.folio_pesada}
            />
          </div>
        </div>
        <div className="col-6">
          <div className="">
            <SelectComponent
              label="Empaque *"
              data={packingList}
              {...register("general.packing", { required: true })}
              errors={errors.general?.packing}
            />
          </div>
        </div>
      </div>
      <div className="row mb-5">
        <div className="col-4">
          <div className="">
            <InputComponent
              label="No. de cajas *"
              {...register("general.total_cajas", {
                required: true,
                pattern: {
                  value: /^\d+(\.\d{1,2})?$/,
                  message:
                    "Solo se permiten números y un máximo de dos decimales",
                },
              })}
              errors={errors.general?.total_cajas}
            />
          </div>
        </div>
        <div className="col-4">
          <div className="">
            <InputComponent
              label="Peso neto *"
              {...register("general.peso_neto", {
                required: true,
                pattern: {
                  value: /^\d+(\.\d{1,2})?$/,
                  message:
                    "Solo se permiten números y un máximo de dos decimales",
                },
              })}
              errors={errors.general?.peso_neto}
            />
          </div>
        </div>
        <div className="col-4">
          <div className="">
            <InputComponent
              label="Peso por caja *"
              {...register("general.peso_caja", {
                required: true,
                pattern: {
                  value: /^\d+(\.\d{1,2})?$/,
                  message:
                    "Solo se permiten números y un máximo de dos decimales",
                },
              })}
              errors={errors.general?.peso_caja}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="">
            <TextareaComponent
              label="Observaciones *"
              {...register("general.observaciones", { required: true })}
              errors={errors.general?.observaciones}
            />
          </div>
        </div>
      </div>

      <div className="separator my-10"></div>

      <div className="d-flex align-items-center mb-5">
        <h4 className="fw-bold d-flex align-items-center text-gray-900">
          Registro por productor
        </h4>

        <button
          className="btn btn-bg-info text-white ms-auto"
          type="button"
          onClick={() => setShowModal({ data: null, show: true })}
        >
          <i className="fa-solid fa-plus text-white"></i> Agregar productor
        </button>
      </div>
      {/* <p className="text-muted fw-semibold fs-6">Lorem ipsum</p> */}

      <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed p-6 mb-5">
        <i className="ki-duotone ki-design-1 fs-2tx text-primary me-4" />
        <div className="d-flex flex-stack flex-grow-1">
          <div className="fw-semibold">
            <div className="fs-6 text-gray-700">
              Ingresa el detalle de las cajas por productor. Recuerda que la
              suma total de las cajas debe coincidir con el número capturado en
              "No. de cajas"
            </div>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover table-rounded table-striped border gy-7 gs-7">
          <thead>
            <tr className="fw-semibold fs-6 text-gray-800 border-bottom-2 border-gray-200">
              <th>Productor</th>
              <th>Huerta</th>
              <th>Cajas</th>
              <th>Peso x caja</th>
              <th>Kg totales</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field: any, index: number) => (
              <tr key={field.id}>
                <td className="align-middle">{field.customer_name}</td>
                <td className="align-middle">{field.field_name}</td>
                <td className="align-middle">
                  {UtilsService.numberFormat(field.cajas)}
                </td>
                <td className="align-middle">
                  {UtilsService.numberFormat(field.peso_caja)}
                </td>
                <td className="align-middle">
                  {UtilsService.numberFormat(field.kg_totales)}
                </td>
                <td className="align-middle text-end">
                  <button
                    className="btn btn-icon btn-secondary me-5 "
                    onClick={() =>
                      setShowModal({ data: { ...field, index }, show: true })
                    }
                  >
                    <i className="far fa-pen-to-square"></i>
                  </button>
                  <button
                    className="btn btn-icon btn-danger me-5 "
                    onClick={() => remove(index)}
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ms-auto mt-8">
        <Button
          className="btn btn-primary"
          disabled={!isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(submit)}
        >
          Guardar
        </Button>
      </div>

      <CustomerForm
        show={showModal.show}
        data={showModal.data}
        setShow={() => setShowModal({ data: null, show: false })}
        onChange={(data: any) => {
          data.index >= 0 ? update(data.index, data) : prepend(data);
        }}
      />
    </>
  );
}

export default Step1;
